/**
 * @file ReturnForChangesDrawer.jsx
 * @module ui/src/components/admin/ReturnForChangesDrawer
 * @description
 *   Renders the structured CA-2 Return for Changes workflow for full-plan and
 *   amendment reviews. Captures a categorized governance reason, mandatory
 *   reviewer comment, and optional section attachment without optimistic state.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S777
 *
 * @architecture
 *   Locked decisions : #2
 *   Temporal invariants : none
 *   Design principles : DP-10, DP-13
 *   Cross-cutting contracts : XC-2
 *
 * @domain-rules
 *   - The action is always "Return for Changes" because CA-2 returns the complete document to CA-1 for correction rather than rejecting a sub-document.
 *   - A category and meaningful reviewer comment are mandatory because the return reason persists in governance history.
 *   - A section attachment is optional because a governance concern may apply to the document as a whole.
 *   - Full-plan versus amendment return routing depends only on amendmentId === null so document type is never inferred from workflow state.
 *   - Governance mutations remain server-authoritative; onReturn fires only after an HTTP 200 response.
 *   - Eligibility and authority are revalidated by the server; this drawer does not compute approval authority.
 *
 * @wiring-s777
 *   ep 10 POST /api/v1/plans/{id}/return — WIRED S777. Status 200.
 *   ep 15 POST /api/v1/plans/{id}/amendments/{aid}/return — WIRED S777. Status 200.
 *   Request body fixed S777: category→returnCategory, comment→returnComment, sectionKey→sectionAttachment.
 *   Routing: amendmentId === null → ep 10. amendmentId !== null → ep 15.
 *
 * @migration-flags
 *   - none
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group CA-2 batch delivery
 *   1.1.0  S777  Fixed request body field names (returnCategory/returnComment/sectionAttachment). Removed AI ref. CRLF→LF.
 */

import PropTypes from 'prop-types';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * @typedef {Object} ReturnForChangesDrawerProps
 * @property {number} planId
 * @property {string|null} amendmentId
 * @property {Function} onReturn
 * @property {Function} onCancel
 */

const RETURN_CATEGORIES = [
  { value: 'economic_configuration', label: 'Economic configuration' },
  { value: 'assignment_scope', label: 'Assignment scope' },
  { value: 'effective_dates_temporal', label: 'Effective dates / temporal' },
  { value: 'rate_table_curve', label: 'Rate table / curve' },
  { value: 'modifier_configuration', label: 'Modifier configuration' },
  { value: 'data_readiness', label: 'Data readiness concern' },
  { value: 'documentation_rationale', label: 'Documentation / rationale' },
  { value: 'other', label: 'Other' },
];

const SECTION_OPTIONS = [
  { value: '', label: '(No section — general comment)' },
  { value: 'overview', label: 'Overview' },
  { value: 'identity', label: 'Identity' },
  { value: 'credit_allocation', label: 'Credit Allocation' },
  { value: 'measurement_economics', label: 'Measurement & Economics' },
  { value: 'payout_adjustments', label: 'Payout Adjustments' },
  { value: 'settlement', label: 'Settlement' },
  { value: 'assignment_scope', label: 'Assignment Scope' },
  { value: 'review_governance', label: 'Review & Governance' },
];

const MIN_COMMENT_LENGTH = 3;

const drawerStyle = {
  position: 'fixed',
  insetBlock: 0,
  insetInlineEnd: 0,
  width: 'min(560px, 100%)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  background: 'rgb(var(--color-surface))',
  color: 'rgb(var(--color-text))',
  borderInlineStart: '1px solid rgb(var(--color-border))',
  boxShadow: '-12px 0 32px rgb(var(--color-border))',
  fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
};

const fieldStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid rgb(var(--color-border))',
  borderRadius: '6px',
  background: 'rgb(var(--color-bg))',
  color: 'rgb(var(--color-text))',
  fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
  fontSize: 'var(--font-sz-base)',
  padding: '10px 12px',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  color: 'rgb(var(--color-text))',
  fontWeight: 600,
};

/**
 * Structured CA-2 return workflow for a full plan or amendment.
 *
 * @param {ReturnForChangesDrawerProps} props
 * @returns {JSX.Element}
 */
function ReturnForChangesDrawer({
  planId,
  amendmentId = null,
  onReturn,
  onCancel,
}) {
  // WHY: category is persisted with the governance return so CA-1 knows the class of correction requested.
  const [category, setCategory] = useState('');

  // WHY: a reviewer-authored explanation is mandatory because a categorical key alone is insufficient governance rationale.
  const [comment, setComment] = useState('');

  // WHY: section attachment is optional navigation metadata that can direct CA-1 to the affected structured document section.
  const [sectionAttachment, setSectionAttachment] = useState('');

  // WHY: governance mutations cannot be double-submitted or reflected optimistically while the server is authoritative.
  const [submitting, setSubmitting] = useState(false);

  // WHY: server failures must remain visible inside the workflow instead of escaping as thrown UI errors.
  const [error, setError] = useState('');

  // WHY: initial focus must move into the drawer for keyboard and screen-reader users when the modal workflow opens.
  const firstFieldRef = useRef(null);

  // WHY: focus returns to the invoking control on close so keyboard users retain their review location.
  const triggerRef = useRef(null);

  // WHY: the drawer itself is the focus-trap boundary for this modal governance workflow.
  const drawerRef = useRef(null);

  // WHY: trim-based validation prevents whitespace-only governance rationale from satisfying the required comment contract.
  const trimmedComment = useMemo(() => comment.trim(), [comment]);

  // WHY: submission is available only when all mandatory governance inputs are meaningfully populated.
  const canSubmit = category !== ''
    && trimmedComment.length >= MIN_COMMENT_LENGTH
    && !submitting;

  // WHY: the drawer behaves as a modal focus scope, handles Escape cancellation, and restores focus to the trigger when unmounted.
  useEffect(() => {
    triggerRef.current = document.activeElement;

    const focusTimer = window.setTimeout(() => {
      firstFieldRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      // WHY: Escape must cancel without a server mutation because dismissal is not a governance decision.
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      // WHY: Tab trapping prevents keyboard focus from escaping behind the modal drawer while it is active.
      if (event.key === 'Tab' && drawerRef.current) {
        const focusable = Array.from(
          drawerRef.current.querySelectorAll(
            'button:not([disabled]), select:not([disabled]), textarea:not([disabled]), '
            + 'input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
          ),
        );

        // WHY: no trapping work is needed if the rendered drawer has no focusable descendants.
        if (focusable.length === 0) {
          return;
        }

        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];

        // WHY: Shift+Tab on the first control must wrap to the final control within the drawer.
        if (event.shiftKey && document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        // WHY: Tab on the final control must wrap to the first control within the drawer.
        } else if (!event.shiftKey && document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);

      // WHY: returning focus to the trigger preserves review continuity after either cancellation or successful return.
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [onCancel]);

  // WHY: category changes clear stale server errors because the reviewer is actively correcting the return request.
  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
    setError('');
  };

  // WHY: comment changes clear stale server errors while preserving the mandatory governance rationale field.
  const handleCommentChange = (event) => {
    setComment(event.target.value);
    setError('');
  };

  // WHY: section selection is navigation metadata only and must never become a prerequisite for returning the document.
  const handleSectionAttachmentChange = (event) => {
    setSectionAttachment(event.target.value);
    setError('');
  };

  // WHY: cancellation closes the workflow immediately because no governance mutation has occurred.
  const handleCancel = () => {
    onCancel();
  };

  // WHY: return submission sends exactly one server-authoritative governance mutation and calls onReturn only after HTTP 200.
  const handleSubmit = async (event) => {
    event.preventDefault();

    // WHY: defensive validation prevents programmatic submission from bypassing mandatory category/comment requirements.
    if (!category || trimmedComment.length < MIN_COMMENT_LENGTH || submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    // WHY: ep 10 and ep 15 routers both require returnCategory, returnComment, sectionAttachment.
    // Router-confirmed S777 from plan_governance_router.py (ep10/15 AmendmentReturnRequest/PlanReturnRequest).
    const requestBody = {
      returnCategory: category,
      returnComment: trimmedComment,
      ...(sectionAttachment ? { sectionAttachment } : {}),
    };

    const endpoint = amendmentId === null
      ? `/api/v1/plans/${planId}/return`
      : `/api/v1/plans/${planId}/amendments/${amendmentId}/return`;

    try {
      // WHY: amendmentId === null is the sole domain discriminator between full-plan and amendment return routes.
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      // WHY: only HTTP 200 constitutes confirmed governance success under the component contract.
      if (response.status !== 200) {
        let message = 'Unable to return this document for changes. Please try again.';

        try {
          const payload = await response.json();
          message = payload?.detail || payload?.message || message;
        } catch {
          // WHY: a non-JSON server failure still receives a stable user-facing error rather than propagating a parse failure.
        }

        setError(message);
        return;
      }

      // WHY: the parent is notified only after the server confirms the governance mutation, satisfying DP-10.
      onReturn();
    } catch {
      setError('Unable to return this document for changes. Please try again.');
    } finally {
      // WHY: resetting the mutation flag after completion permits retry after failure without inventing local governance state.
      setSubmitting(false);
    }
  };

  return (
    <aside
      ref={drawerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="return-for-changes-title"
      aria-describedby="return-for-changes-description"
      style={drawerStyle}
    >
      <header className="modifier-manager-header">
        <div>
          <h2 id="return-for-changes-title">Return for Changes</h2>
          <p
            id="return-for-changes-description"
            style={{ color: 'rgb(var(--color-text-muted))', marginBlockEnd: 0 }}
          >
            Provide a categorized reason and comment for CA-1. You may optionally
            attach the return to a document section.
          </p>
        </div>
      </header>

      <form
        className="modifier-manager-body"
        onSubmit={handleSubmit}
        style={{ flex: 1, overflowY: 'auto' }}
      >
        <label style={labelStyle}>
          Return category
          <select
            ref={firstFieldRef}
            value={category}
            onChange={handleCategoryChange}
            required
            aria-required="true"
            style={fieldStyle}
          >
            <option value="">Select a category</option>
            {RETURN_CATEGORIES.map((option) => (
              // WHY: each option renders the locked internal key separately from its human-readable governance label.
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          Comment
          <textarea
            value={comment}
            onChange={handleCommentChange}
            required
            aria-required="true"
            minLength={MIN_COMMENT_LENGTH}
            rows={7}
            placeholder="Explain what CA-1 needs to change."
            style={{
              ...fieldStyle,
              resize: 'vertical',
            }}
          />
          <span
            style={{
              color: 'rgb(var(--color-text-muted))',
              fontSize: '0.875em',
              fontWeight: 400,
            }}
          >
            Required. Provide enough detail for CA-1 to act on the return.
          </span>
        </label>

        <label style={labelStyle}>
          Attach to section
          <select
            value={sectionAttachment}
            onChange={handleSectionAttachmentChange}
            style={fieldStyle}
          >
            {SECTION_OPTIONS.map((option) => (
              // WHY: an empty value intentionally represents a document-level return with no section deep-link.
              <option key={option.value || 'general'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {/* WHY: server mutation failures remain inline so the reviewer can correct or retry without losing entered governance rationale. */}
        {error && (
          <div
            className="modifier-manager-inline-error"
            role="alert"
          >
            {error}
          </div>
        )}

        <footer className="modifier-manager-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="primary-button"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
          >
            {submitting ? 'Returning…' : 'Return for Changes'}
          </button>
        </footer>
      </form>
    </aside>
  );
}

ReturnForChangesDrawer.propTypes = {
  planId: PropTypes.number.isRequired,
  amendmentId: PropTypes.string,
  onReturn: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ReturnForChangesDrawer;

/**
 * @example
 * <ReturnForChangesDrawer
 *   planId={481}
 *   amendmentId={null}
 *   onReturn={() => refreshApprovalQueue()}
 *   onCancel={() => setReturnDrawerOpen(false)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - Locked decision #2: the complete plan/amendment document is returned as one governance object.
 * - DP-10: no local approval/return outcome is committed before the server returns HTTP 200.
 * - DP-13 / XC-2: authority is not computed here; the backend re-validates the mutation.
 * - Full-plan versus amendment route selection depends only on amendmentId === null.
 * - Category and meaningful comment are mandatory; section attachment remains optional.
 * - User-facing action terminology is "Return for Changes"; no reject semantics are introduced.
 * - DP-8 audit: no sub-document or per-modifier CA-2 approval language is rendered.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - none — amendment_log and plan governance structures exist in DDL r57.
 */

/**
 * ACCESSIBILITY NOTES
 * - The drawer uses role="dialog" and aria-modal="true" with explicit title/description relationships.
 * - Initial focus moves to the category selector.
 * - Tab and Shift+Tab remain trapped within the active drawer.
 * - Escape invokes cancellation without a server mutation.
 * - Focus returns to the invoking element when the drawer unmounts.
 * - Required fields expose native required and aria-required semantics.
 * - Server errors use role="alert".
 */